import { useTransactionDetails } from '@/apis/transactions/hooks/useTransactionDetails'
import { useApplicantProgramYears } from '@/app/SchoolYearProvider'
import PageLoader from '@/app/common/components/PageLoader/PageLoader'
import { DocumentsViewer } from '@/app/common/components/payment_details_items/documents_viewer'
import PaymentAmountCard from '@/app/common/components/payment_details_items/payment_amount_card'
import { PurseMovementCard } from '@/app/common/components/payment_details_items/purse_movements_card'
import PaymentStatusBadge from '@/app/common/components/student_payment_status_badge/payment_status_badge'
import { BASE_DATE_FORMAT_WITH_TIME } from '@/app/common/constants'
import { CheckCircleIcon } from '@/app/common/icons/arrows'
import { currencyFormatter } from '@/app/common/utilities'
import { FormRowLayout } from '@/app/pages/my_account/components/form_layouts.tsx'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { FormRowLabel, FormRowValue } from '@/common/components/FormRow.tsx'
import { format } from 'date-fns'
import { useState } from 'react'
import Image from '../../common/components/Image/Image'
import { PaymentItemsList } from '../payments/pages/payment_details/_components/PaymentItemList'

function TransactionDetailsPage() {
  const queryId = location.pathname.split('/')[2]
  const transactionId = queryId ? parseInt(queryId) : undefined
  const { selectedProgramId } = useApplicantProgramYears()
  const query = useTransactionDetails(selectedProgramId, transactionId)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  // Function to close the full-size image modal
  const closeModal = () => {
    setSelectedImage(null)
  }

  return (
    <PageLoader query={query}>
      {(data) => {
        return (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Transaction Details</h2>
                <PaymentStatusBadge
                  paymentType={data.type}
                  paymentStatus={data.status}
                />
              </CardHeader>
              <CardContent>
                <FormRowLayout>
                  <FormRowLabel text="Transaction ID" />
                  <FormRowValue text={data.transaction_id.toString()} />
                </FormRowLayout>
                {data.transaction_type && (
                  <FormRowLayout>
                    <FormRowLabel text="Transaction Type" />
                    <FormRowValue text={data.transaction_type} />
                  </FormRowLayout>
                )}
                <FormRowLayout>
                  <FormRowLabel text="Transaction Date" />
                  <FormRowValue
                    text={format(
                      data.created_datetime,
                      BASE_DATE_FORMAT_WITH_TIME
                    )}
                  />
                </FormRowLayout>
                {data.provider_name && (
                  <FormRowLayout>
                    <FormRowLabel text="Provider Name" />
                    <FormRowValue text={data.provider_name} />
                  </FormRowLayout>
                )}

                <FormRowLayout>
                  <FormRowLabel text="Applicant Name" />
                  <FormRowValue text={data.applicant_name} />
                </FormRowLayout>

                <FormRowLayout>
                  <FormRowLabel text="Student(s) Name" />
                  <FormRowValue
                    text={data.students
                      .map((student) => student.student_name)
                      .join(', ')}
                  />
                </FormRowLayout>
                <FormRowLayout>
                  <FormRowLabel text="Invoice Quote" />
                  <FormRowValue
                    text={data.invoice_quote ?? "Not Added"}
                  />
                </FormRowLayout>
                <FormRowLayout>
                  <FormRowLabel text="Tracking ID" />
                  <FormRowValue
                    text={data.tracking_number ?? "Not Added"}
                  />
                </FormRowLayout>
              </CardContent>
            </Card>
            <PaymentAmountCard payment={data} />
            {data.expense_categories && data.expense_categories.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-bold">Categories</h2>
                </CardHeader>
                <CardContent>
                  {data.expense_categories.map((category) => {
                    return (
                      <FormRowLayout>
                        <FormRowLabel text={category.name} />
                        <FormRowValue
                          text={`${currencyFormatter.format(category.amount)}`}
                        />
                      </FormRowLayout>
                    )
                  })}
                </CardContent>
              </Card>
            )}
            {data?.payment_items?.length > 0 && (
              <PaymentItemsList payment_items={data.payment_items} />
            )}

            <PurseMovementCard purses={data.purse_movements ?? []} />
            {Boolean(data.has_appealed) && data.status === 'Approved' && (
              <Card>
                <CardHeader className="text-lg font-bold space-x-2">
                  <CheckCircleIcon className="text-primary mr-2" />
                  Approval Reason
                </CardHeader>
                <CardContent>{data.last_message}</CardContent>
              </Card>
            )}
            <DocumentsViewer documents={data.payment_documents} />

            {/* Modal for full-size image */}
            {selectedImage && (
              <div
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                onClick={closeModal}
              >
                <div className="max-w-4xl max-h-screen p-4">
                  <Image
                    path={selectedImage}
                    className="max-w-full max-h-full object-contain"
                  />
                  <button
                    className="absolute top-4 right-4 bg-white rounded-full p-2 text-gray-800 hover:bg-gray-200"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }}
    </PageLoader>
  )
}

export default TransactionDetailsPage
